import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  ListSubheader
} from '@mui/material';
import currencyService from '../../services/currencyService';

/**
 * CurrencySelect - A searchable currency dropdown component
 *
 * Features:
 * - Fetches currencies from centralized API
 * - Type-ahead search by code, name, or region
 * - Groups currencies: Common (major) currencies first, then All
 * - Shows currency code, name, and symbol
 *
 * Props:
 * - value: Selected currency code (e.g., 'USD')
 * - onChange: Callback when selection changes (receives currency code)
 * - label: Input label (default: 'Currency')
 * - placeholder: Placeholder text
 * - size: 'small' | 'medium' (default: 'small')
 * - fullWidth: boolean (default: false)
 * - required: boolean (default: false)
 * - disabled: boolean (default: false)
 * - error: boolean (default: false)
 * - helperText: Helper text below input
 * - showMajorOnly: Only show major currencies (default: false)
 * - sx: Additional styles
 */
const CurrencySelect = ({
  value,
  onChange,
  label = 'Currency',
  placeholder = 'Search currency...',
  size = 'small',
  fullWidth = false,
  required = false,
  disabled = false,
  error = false,
  helperText,
  showMajorOnly = false,
  sx = {}
}) => {
  const [currencies, setCurrencies] = useState({ major: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');

  // Load currencies on mount
  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    setLoading(true);
    try {
      const data = await currencyService.getCurrencies();
      setCurrencies({
        major: data.major || [],
        all: data.all || []
      });
    } catch (err) {
      console.error('Failed to load currencies:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build options list with grouping
  const options = useMemo(() => {
    if (showMajorOnly) {
      return currencies.major.map(c => ({
        ...c,
        group: 'Common'
      }));
    }

    // Major currencies first, then others
    const majorCodes = new Set(currencies.major.map(c => c.code));
    const majorOptions = currencies.major.map(c => ({
      ...c,
      group: 'Common'
    }));

    const otherOptions = currencies.all
      .filter(c => !majorCodes.has(c.code))
      .map(c => ({
        ...c,
        group: 'All Currencies'
      }));

    return [...majorOptions, ...otherOptions];
  }, [currencies, showMajorOnly]);

  // Find the selected currency object
  const selectedCurrency = useMemo(() => {
    if (!value) return null;
    return options.find(c => c.code === value) || null;
  }, [value, options]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.code : '');
  };

  // Filter function for search
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return options;

    const query = inputValue.toLowerCase();
    return options.filter(option =>
      option.code.toLowerCase().includes(query) ||
      option.name.toLowerCase().includes(query) ||
      (option.region && option.region.toLowerCase().includes(query))
    );
  };

  // Render option in dropdown
  const renderOption = (props, option) => (
    <Box
      component="li"
      {...props}
      key={option.code}
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      <Typography
        component="span"
        sx={{ fontWeight: 'medium', minWidth: 40 }}
      >
        {option.code}
      </Typography>
      <Typography
        component="span"
        color="text.secondary"
        sx={{ flex: 1 }}
      >
        {option.name}
      </Typography>
      <Typography
        component="span"
        color="text.secondary"
        sx={{ fontSize: '0.85em' }}
      >
        {option.symbol}
      </Typography>
    </Box>
  );

  // Render selected value in input
  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    return option.code || '';
  };

  // Group header renderer
  const renderGroup = (params) => [
    <ListSubheader
      key={params.key}
      component="div"
      sx={{
        bgcolor: 'background.paper',
        fontWeight: 'bold',
        color: 'text.secondary',
        lineHeight: '32px'
      }}
    >
      {params.group}
    </ListSubheader>,
    params.children
  ];

  return (
    <Autocomplete
      value={selectedCurrency}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newValue) => setInputValue(newValue)}
      options={options}
      groupBy={(option) => option.group}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      renderOption={renderOption}
      renderGroup={renderGroup}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      isOptionEqualToValue={(option, value) => option.code === value?.code}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            startAdornment: selectedCurrency ? (
              <Chip
                label={selectedCurrency.symbol}
                size="small"
                sx={{ mr: 0.5, height: 20, fontSize: '0.75rem' }}
              />
            ) : null
          }}
        />
      )}
      sx={{
        minWidth: 150,
        ...sx
      }}
      ListboxProps={{
        sx: { maxHeight: 300 }
      }}
      noOptionsText="No currencies found"
      clearOnBlur
      handleHomeEndKeys
    />
  );
};

export default CurrencySelect;
